<script setup lang="ts" generic="TForm extends { Field: Component }">
  /**
   * The shared inherit/override wrapper used by every per-setting
   * modal. Receives a tanstack form + a string-enum field name that
   * toggles between 'inherit' and 'custom' modes. The custom branch's
   * setting-specific fields go in the `#custom` slot.
   *
   * Doesn't know anything about which setting it's hosting — the
   * per-setting modal owns that. This component just owns the
   * inherit/override UX.
   *
   * When the current agency can't revert to inherit (e.g. the root
   * agency with `preventRemovalWhenLayerIsAgencyRoot`), renders the
   * custom fields unconditionally without the radio toggle.
   */

  import type { Component } from 'vue'
  import {
    FormField,
    RadioCardGroup,
    RadioCardGroupItem
  } from '@gat-ship/vue-components'
  import { IconArrowDownToLine, IconEdit } from '@gat-ship/icons'

  defineProps<{
    form: TForm
    /** The field name on `form` that holds the 'inherit' | 'custom' value. */
    name: string
    /** Label for the inherit option — "Inherit From" or "Use Gatship Default". */
    inheritLabel: string
    /** True when the user is allowed to pick inherit. */
    canRevertToInherit: boolean
    /** Display text for the inherited source (e.g. "Europe Region"). */
    inheritedFromText?: string
    /** Display text for the inherited value (e.g. "USD"). */
    inheritedValueText?: string
  }>()

  defineSlots<{
    /** The setting-specific fields rendered under the "Override" option. */
    custom?: () => unknown
  }>()
</script>

<template>
  <FormField
    v-if="canRevertToInherit"
    :form="form"
    :name="name"
    :input-group="false"
  >
    <template #default="{ field }">
      <RadioCardGroup
        :model-value="field.state.value"
        @update:model-value="field.handleChange"
      >
        <RadioCardGroupItem
          value="inherit"
          :label="inheritLabel"
          description="Get setting from parent agencies"
          :icon="IconArrowDownToLine"
        >
          <template v-if="inheritedFromText || inheritedValueText" #description>
            <span v-if="inheritedFromText" class="block">
              {{ inheritedFromText }}
            </span>
            <span
              v-if="inheritedValueText"
              class="text-foreground block font-medium"
            >
              → {{ inheritedValueText }}
            </span>
          </template>
        </RadioCardGroupItem>
        <RadioCardGroupItem
          value="custom"
          label="Override"
          description="Override the setting for this agency"
          :icon="IconEdit"
        >
          <template #footer="{ selected }">
            <div v-if="selected" class="flex w-full flex-col gap-4">
              <slot name="custom" />
            </div>
          </template>
        </RadioCardGroupItem>
      </RadioCardGroup>
    </template>
  </FormField>
  <div v-else class="flex w-full flex-col gap-4">
    <slot name="custom" />
  </div>
</template>
